import React, { useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import isPrimaryClickEvent from '@medflex/utils/isPrimaryClickEvent';
import { PencilSquareIcon } from '@heroicons/react/20/solid';
import useClassNames from '@medflex/hooks/useClassNames';
import { USER_TYPES } from '@medflex/utils/constants';
import ContactList from '../ContactList';
import Button from '../Button';
import ContactActionMenu from '../ContactActionMenu';
import ScrollableTabs from '../ScrollableTabs';
import InvitationButton from '../AddressBook/InvitationButton';
import Spinner from '../Spinner';
import { Subtitle, Title } from '../HighlightableInfos';
import PhysicianChatContactListEmptyState from './PhysicianChatContactListEmptyState';
import WindowingContainer from '../WindowingContainer';

const ChatListTabCount = ({ active, count }) => {
  return (
    <div className="-mb-px ml-7 inline-flex h-12 items-center justify-start border-blue-600 cursor-pointer first:ml-0">
      <div
        className={useClassNames(() => [
          'inline ml-2 rounded-full px-3 [&:empty]:hidden',
          active && 'bg-blue-100 text-blue-600',
          !active && 'bg-slate-300 text-slate-500',
        ])}
      >
        {count > 0 && <span>{count}</span>}
      </div>
    </div>
  );
};

ChatListTabCount.propTypes = {
  active: PropTypes.bool.isRequired,
  count: PropTypes.number.isRequired,
};

const PhysicianChatContactList = ({
  contacts,
  onClickCompose,
  selectedContactId,
  onClickContact,
  onViewContactInfos,
  onCreateSubcontact,
  onSharePatientContact,
  onOpenMainContact,
  onToggleArchive,
  onClickSubtitleShowMore,
  onStartQuestionnaire,
  hasQuestionnaireFeatureAccess,
  filter,
  onFilterChanged,
  count,
}) => {
  const { t } = useTranslation('components', { keyPrefix: 'PhysicianChatContactList' });
  const selectedContactRef = useRef();

  const joinedContacts = useMemo(
    () =>
      Object.keys(contacts).length > 0 && [
        ...contacts.default.contacts,
        ...contacts.users.contacts,
      ],
    [contacts],
  );

  const createContactClickHandler = contact => event => {
    if (!event.defaultPrevented && isPrimaryClickEvent(event) && selectedContactId !== contact.id) {
      event.preventDefault();
      onClickContact(event, contact);
    }
  };

  useEffect(() => {
    const { current: selectedContact } = selectedContactRef;
    if (selectedContact && selectedContact.scrollIntoView) {
      selectedContact.scrollIntoView();
    }
  }, [selectedContactRef.current]);

  const tabs = [
    {
      key: 'all',
      label: t('title_bar.all'),
    },
    {
      key: 'unread',
      label: t('title_bar.unread'),
      additionalChild: <ChatListTabCount active={filter === 'unread'} count={count.unread} />,
    },
    {
      key: 'important',
      label: t('title_bar.important'),
      additionalChild: <ChatListTabCount active={filter === 'important'} count={count.important} />,
    },
  ];

  return (
    <section
      data-component="PhysicianChatContactList"
      className="flex flex-col flex-grow overflow-hidden flex-1"
    >
      <div className="flex flex-col px-4 pt-4 border-slate-300 border-b-1 bg-slate-50 space-y-4">
        <div className="flex justify-between items-center self-stretch w-full">
          <span className="text-slate-900 text-xl font-semibold">{t('title_bar.label')}</span>
          <Button kind="link" onClick={onClickCompose}>
            <PencilSquareIcon className="w-7 h-7" />
          </Button>
        </div>
        <ScrollableTabs tabs={tabs} active={filter} onClick={onFilterChanged} />
      </div>

      {joinedContacts ? (
        <WindowingContainer list={joinedContacts} selectedListItemId={selectedContactId}>
          {({ windowedList, ref, listWindowLimit }) => (
            <div ref={ref} className="flex flex-col h-full flex-1 lg:border-r overflow-y-auto">
              {windowedList.map(contact => {
                const canToggleArchive =
                  contact.pairedAt && ['patient', 'physician'].includes(contact.type);
                const borderBottom =
                  contacts.default.contacts.length > 0 &&
                  contact.id === contacts.default.contacts.slice(-1)[0].id
                    ? 'border-b-slate-300 border-b-1'
                    : '';
                return (
                  <ContactList.Item
                    key={contact.id + filter}
                    wrapperClassName={borderBottom}
                    isOnline={contact.isOnline}
                    deactivated={contact.user.deactivated}
                    avatarSrc={contact.user.avatar}
                    avatarInitials={contact.user.initials}
                    innerRef={selectedContactId === contact.id ? selectedContactRef : null}
                    selected={selectedContactId === contact.id}
                    contactUserId={contact.user.id}
                    notificationCount={contact.messages && contact.messages.unread}
                    lastAtMessage={contact.messages && contact.messages.lastAt}
                    lastCheckedMessageId={contact.lastCheckedMessageId}
                    title={<Title contact={contact} />}
                    subtitle={
                      <Subtitle
                        contact={contact}
                        onClickContactInfo={() => onClickSubtitleShowMore(contact.user.id)}
                        onClickMainContact={() => onOpenMainContact(contact.mainContact.id)}
                      />
                    }
                    onClick={createContactClickHandler(contact)}
                    enabled
                    showBorder
                    // eslint-disable-next-line react/no-unstable-nested-components
                    ActionMenu={props => (
                      <ContactActionMenu
                        /* eslint-disable-next-line */
                    {...props}
                        contactUserId={contact.user.id}
                        contactId={contact.id}
                        canCreateSubcontact={contact.canCreateSubcontact}
                        canReadContactInfos={contact.type !== 'session-chat'}
                        canSharePatientContact={contact.canSharePatientContact}
                        canStartQuestionnaire={
                          hasQuestionnaireFeatureAccess &&
                          contact.type === USER_TYPES.PATIENT &&
                          !!contact.pairedAt
                        }
                        onViewContactInfos={onViewContactInfos}
                        onCreateSubcontact={onCreateSubcontact}
                        onSharePatientContact={onSharePatientContact}
                        onStartQuestionnaire={onStartQuestionnaire}
                        subtitle={contact.subtitle}
                        canArchive={canToggleArchive && !contact.archivedAt}
                        canDearchive={canToggleArchive && contact.archivedAt}
                        onArchive={() => onToggleArchive(contact)}
                        onDearchive={() => onToggleArchive(contact)}
                      />
                    )}
                  />
                );
              })}
              {listWindowLimit < (joinedContacts && joinedContacts.length) && (
                <div className="mb-2 w-full text-center">
                  <Spinner size="lg" />
                </div>
              )}
            </div>
          )}
        </WindowingContainer>
      ) : (
        <PhysicianChatContactListEmptyState filter={filter} />
      )}

      <InvitationButton onClick={onClickCompose}>{t('new_message')}</InvitationButton>
    </section>
  );
};

const contactsPropType = PropTypes.arrayOf(
  PropTypes.shape({
    canCreateSubcontact: PropTypes.bool,
    canSharePatientContact: PropTypes.bool,
    user: PropTypes.shape({
      avatar: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      dateOfBirth: PropTypes.string,
      fullNameWithTitle: PropTypes.string.isRequired,
      subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      initials: PropTypes.string,
      id: PropTypes.string.isRequired,
    }),
    createdAt: PropTypes.string.isRequired,
    enabled: PropTypes.bool,
    id: PropTypes.string,
  }),
);

PhysicianChatContactList.propTypes = {
  onFilterChanged: PropTypes.func,
  filter: PropTypes.oneOf(['all', 'important', 'unread']),
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.array,
    PropTypes.object,
  ]),
  contacts: PropTypes.shape({
    default: PropTypes.shape({ contacts: contactsPropType }),
    users: PropTypes.shape({ contacts: contactsPropType }),
  }),
  onClickCompose: PropTypes.func,
  selectedContactId: PropTypes.string,
  onClickContact: PropTypes.func,
  onClickSubtitleShowMore: PropTypes.func,
  onViewContactInfos: PropTypes.func,
  onCreateSubcontact: PropTypes.func,
  onStartQuestionnaire: PropTypes.func,
  onSharePatientContact: PropTypes.func,
  onOpenMainContact: PropTypes.func,
  onToggleArchive: PropTypes.func,
  count: PropTypes.shape({ unread: PropTypes.number, important: PropTypes.number }),
  hasQuestionnaireFeatureAccess: PropTypes.bool,
};

PhysicianChatContactList.defaultProps = {
  filter: 'all',
  className: '',
  contacts: {},
  count: {},
  selectedContactId: null,
  onClickCompose: () => {},
  onClickContact: () => {},
  onClickSubtitleShowMore: () => {},
  onViewContactInfos: () => {},
  onSharePatientContact: () => {},
  onFilterChanged: () => {},
  onOpenMainContact: () => {},
  onToggleArchive: () => {},
  onCreateSubcontact: () => {},
  onStartQuestionnaire: () => {},
  hasQuestionnaireFeatureAccess: false,
};

export default PhysicianChatContactList;
